import { type ModuleId, getModuleQuestions } from '../content'
import { QuestionState, SubSkillProgress } from './types'
import { FamilyMasteryState } from './familyMasteryEngine'
import { GroupId } from '../config/systemConfig'
import { computeSubSkillMastery } from './subSkillMastery'
import { getQuestionStateKey } from './questionAttemptHistory'

/**
 * Skill Aggregation Utility — Taxonomy-Aware with Group-Scoped Progress
 *
 * Uses q.group directly and group-scoped question state keys.
 *
 * Aggregates subSkill-level mastery data into skill-level data for the leerling-facing UI.
 * The adaptive engine continues to work on subSkill level internally.
 *
 * Taxonomy: Module → Skill → SubSkill → FamilyId
 *
 * Example: Getalbegrip M6
 *   skill="breuken" → subSkills: breuken_decimaal, breuken_deel_van, breuken_optellen, breuken_vergelijken
 *   skill="basisrekenen" → subSkills: optellen, aftrekken
 *   skill="getallenleer" → subSkills: positiewaarde, getallen_vergelijken
 *
 * When skill === subSkill (e.g. "verhoudingen"), it's a 1:1 mapping — works fine.
 */

export interface SkillMastery {
  skill: string
  masteryScore: number // 0-100, averaged from subSkills
  questionsAnswered: number // total across subSkills
  correctCount: number // total across subSkills
  trend: 'improving' | 'stable' | 'struggling'
  lastPracticed: number
  subSkills: SubSkillProgress[] // the underlying subSkill data for drill-down
}

/**
 * Discover the skill → subSkill mapping for a module within a group.
 * Uses q.group directly (taxonomy-authoritative).
 */
export function getSkillToSubSkillMap(
  moduleId: ModuleId,
  group: GroupId,
): Map<string, Set<string>> {
  const mapping = new Map<string, Set<string>>()
  const moduleQuestions = getModuleQuestions(moduleId)
  moduleQuestions.forEach((q) => {
    if (!q.skill || !q.subSkill) return
    if (q.group !== group) return
    if (!mapping.has(q.skill)) {
      mapping.set(q.skill, new Set())
    }
    mapping.get(q.skill)!.add(q.subSkill)
  })
  return mapping
}

/**
 * Compute aggregated skill mastery for a module.
 * Averages the subSkill mastery scores within each skill.
 * Only includes skills that have at least one practiced subSkill.
 * Uses group-scoped question states.
 */
export function computeSkillMasteries(
  moduleId: ModuleId,
  group: GroupId,
  questionStates: Record<string, QuestionState>,
  familyMastery?: Record<string, FamilyMasteryState>, // NEW: Optional for backward compat
): SkillMastery[] {
  const skillMap = getSkillToSubSkillMap(moduleId, group)
  const results: SkillMastery[] = []
  skillMap.forEach((subSkillNames, skillName) => {
    // Compute mastery for each subSkill (now uses group-scoped keys internally)
    const subSkillMasteries: SubSkillProgress[] = []
    subSkillNames.forEach((subSkillName) => {
      // Pass familyMastery if available
      const mastery = computeSubSkillMastery(
        moduleId,
        subSkillName,
        questionStates,
        group,
        familyMastery,
      )
      subSkillMasteries.push(mastery)
    })

    // Only include skills where at least one subSkill has been practiced
    const practicedSubSkills = subSkillMasteries.filter(
      (s) => s.questionsAnswered > 0,
    )
    if (practicedSubSkills.length === 0) return

    // Aggregate: average mastery score across practiced subSkills
    const totalScore = practicedSubSkills.reduce(
      (sum, s) => sum + s.masteryScore,
      0,
    )
    const avgScore = Math.round(totalScore / practicedSubSkills.length)

    // Aggregate: sum questions and correct counts
    const totalAnswered = practicedSubSkills.reduce(
      (sum, s) => sum + s.questionsAnswered,
      0,
    )
    const totalCorrect = practicedSubSkills.reduce(
      (sum, s) => sum + s.correctCount,
      0,
    )

    // Aggregate: latest practice time
    const lastPracticed = Math.max(
      ...practicedSubSkills.map((s) => s.lastPracticed),
    )

    // Aggregate trend: if any subSkill is struggling, skill is struggling
    // If majority are improving, skill is improving
    let trend: 'improving' | 'stable' | 'struggling' = 'stable'
    const trendCounts = {
      improving: 0,
      stable: 0,
      struggling: 0,
    }
    practicedSubSkills.forEach((s) => {
      trendCounts[s.trend]++
    })
    if (trendCounts.struggling > 0) {
      trend = 'struggling'
    } else if (trendCounts.improving > trendCounts.stable) {
      trend = 'improving'
    }
    results.push({
      skill: skillName,
      masteryScore: avgScore,
      questionsAnswered: totalAnswered,
      correctCount: totalCorrect,
      trend,
      lastPracticed,
      subSkills: practicedSubSkills,
    })
  })
  return results
}

/**
 * Get the count of active (practiced) skills for a module in a group.
 * Uses group-scoped question states.
 */
export function getActiveSkillCount(
  moduleId: ModuleId,
  group: GroupId,
  questionStates: Record<string, QuestionState>,
  familyMastery?: Record<string, FamilyMasteryState>,
): number {
  return computeSkillMasteries(moduleId, group, questionStates, familyMastery)
    .length
}
